import { forwardRef, type ReactElement, useCallback, useRef } from 'react';

import { VList, type VListHandle } from 'virtua';

import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';
import { composeRefs } from '@iconik-io/shared-lib/src/react/utils/compose_refs';

type TreeProps = {
  className?: string;
  items: ReactElement[];
  itemSize?: number;
  onScroll?: (offset: number) => void;
  onScrollEnds?: () => void;
  aria?: AriaProps;
};

export const Tree = forwardRef<VListHandle, TreeProps>(
  ({ aria, className, itemSize, items, onScroll, onScrollEnds }, ref) => {
    const vListRef = useRef<VListHandle>(null);

    const handleScroll = useCallback(
      (offset: number) => {
        onScroll?.(offset);
        if (vListRef.current) {
          const { viewportSize, scrollSize } = vListRef.current;
          if (scrollSize - (offset + viewportSize) < 200) {
            onScrollEnds?.();
          }
        }
      },
      [onScroll, onScrollEnds],
    );

    return (
      <VList
        className={className}
        count={items.length}
        itemSize={itemSize}
        ref={composeRefs(ref, vListRef)}
        onScroll={handleScroll}
        role="tree"
        {...ariaToReactProps(aria)}
      >
        {items}
      </VList>
    );
  },
);
